import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faArrowLeft,
  faPlus,
  faPen,
  faEye,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { DriverApi, DriverDTO } from './DriverApi';
import { Card, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// TODO hook these from your auth/role store
const canView = (/* user */) => true;
const canEdit = (/* user */) => true;
const canDelete = (/* user */) => true;
const canCreate = (/* user */) => true;

const DriverList: React.FC = () => {
  const navigate = useNavigate();

  const [tenantId, setTenantId] = useState<string>(() => localStorage.getItem('tad.tenantId') || '');
  const [tenantName] = useState<string>(() => localStorage.getItem('tad.tenantName') || '');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [licenseNo, setLicenseNo] = useState('');
  const [active, setActive] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DriverDTO[]>([]);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const fetchData = async (resetPage = false) => {
    if (!tenantId) {
      alert('Please set Tenant (id) first.');
      return;
    }
    setLoading(true);
    try {
      const res = await DriverApi.search(
        {
          tenantId,
          name: name || undefined,
          phone: phone || undefined,
          licenseNo: licenseNo || undefined,
          isActive: active === '' ? undefined : active === 'true',
        },
        resetPage ? 0 : page,
        size,
        'name,asc'
      );
      setRows(res.data.content || []);
      setTotalElements(res.data.totalElements);
      setTotalPages(res.data.totalPages);
      if (resetPage) setPage(0);
    } catch (e: any) {
      alert(e?.response?.data?.message || e.message || 'Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onDelete = async (id?: string) => {
    if (!tenantId || !id) return;
    if (!confirm('Delete this driver?')) return;
    try {
      await DriverApi.remove(tenantId, id);
      fetchData();
    } catch (e: any) {
      alert(e?.response?.data?.message || e.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Local tenant setter like in your GeneralTask UI */}
      <Card>
        <CardContent className="flex items-end gap-3">
          <div className="w-64">
            <label className="block text-xs font-semibold mb-1">Tenant ID</label>
            <input
              className="w-full border rounded px-2 py-1"
              placeholder="UUID"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            />
          </div>
          <button
            className="ml-auto bg-gray-100 hover:bg-gray-200 text-gray-800 rounded px-3 py-2"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back
          </button>
          {canCreate() && (
            <button
              onClick={() => navigate('/driver/create')}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              New Driver
            </button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-sm font-semibold mb-1">Name</label>
            <input
              className="w-full border rounded px-2 py-2"
              placeholder="Search name..."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Phone</label>
            <input
              className="w-full border rounded px-2 py-2"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">License No</label>
            <input
              className="w-full border rounded px-2 py-2"
              placeholder="DL-XXXX..."
              value={licenseNo}
              onChange={(e) => setLicenseNo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Status</label>
            <select
              className="w-full border rounded px-2 py-2 bg-white"
              value={active}
              onChange={(e) => setActive(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button onClick={() => fetchData(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              <FontAwesomeIcon icon={faSearch} className="mr-2" /> Apply Filters
            </button>
            <button
              onClick={() => {
                setName('');
                setPhone('');
                setLicenseNo('');
                setActive('');
                setPage(0);
                fetchData(true);
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Reset
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Phone</th>
                      <th className="py-2 pr-4">License</th>
                      <th className="py-2 pr-4">Valid Till</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="py-2 pr-4">{r.name}</td>
                        <td className="py-2 pr-4">{r.phone || '-'}</td>
                        <td className="py-2 pr-4">{r.licenseNo}</td>
                        <td className="py-2 pr-4">{(r.licenseValidTill || '').slice(0, 10)}</td>
                        <td className="py-2 pr-4">
                          <span className={cn('font-medium', r.isActive ? 'text-green-700' : 'text-gray-500')}>
                            {r.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex items-center gap-2 justify-end">
                            {canView() && (
                              <button
                                className="text-blue-600 hover:underline"
                                onClick={() => navigate(`/driver/${r.id}`)}
                                title="View"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                            )}
                            {canEdit() && (
                              <button
                                className="text-amber-600 hover:underline"
                                onClick={() => navigate(`/driver/${r.id}/edit`)}
                                title="Edit"
                              >
                                <FontAwesomeIcon icon={faPen} />
                              </button>
                            )}
                            {canDelete() && (
                              <button className="text-red-600 hover:underline" onClick={() => onDelete(r.id)} title="Delete">
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-gray-500 py-10">
                          No drivers found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-gray-600">
                  Page {page + 1} of {Math.max(totalPages, 1)} • {totalElements} result(s)
                </p>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => setPage((p) => Math.max(p - 1, 0))}
                    disabled={page === 0}
                  >
                    Prev
                  </button>
                  <button
                    className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                    disabled={page + 1 >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverList;